import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import uuid4 from 'uuid4';

import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import useNoPermissionToast from 'hooks/useNoPermissionToast';
import useScopedUserPowerLevels from 'hooks/useScopedUserPowerLevels/useScopedUserPowerLevels';
import { WorkspaceRoleV2 } from 'modules/CreateWorkspaceModal/types';
import { clampRoleEditorWorkspacePermissions } from 'modules/CreateWorkspaceModal/utils';
// Pure function despite the `use*` prefix (spec/tech.md §2.2 — "not a hook"). Imported under a
// non-`use` alias so it can be invoked inside the async submit callback without tripping
// `react-hooks/rules-of-hooks` for a value-dependent call site.
import getScopedPowerLevels from 'modules/CreateWorkspaceModal/WorkspaceRoleCreate/useScopedPowerLevels';
import useWorkspacesContext from 'modules/Workspaces/WorkspacesProvider/useWorkspacesContext';
import logger from 'utils/logging/faro';
import { CustomMeedioMatrixEvents } from 'utils/matrixUtils';
import { PermissionId } from 'utils/powerLevelsConfig';
import { WORKSPACE_ROLES_EVENT_VERSION } from 'utils/workspaceRolesEvent';

import { DefaultRoleTypesEnum } from './utils';

type CreateWorkspaceRolePayload = {
  workspaceId: string | null;
  name: string;
  permissions: Record<PermissionId, boolean>;
  defaultRoleType?: DefaultRoleTypesEnum;
};

const useCreateWorkspaceRole = () => {
  const { t } = useTranslation();
  const { matrixClient } = useMatrixContext();
  const { pushToast } = useToast();
  const pushNoPermissionToast = useNoPermissionToast();
  const [isLoading, setIsLoading] = useState(false);
  const {
    roleAssignments,
    workspaceRoles,
    workspacePermissions,
    workspaceId: contextWorkspaceId,
  } = useWorkspacesContext({ requireWorkspaceId: true });
  const actorPowerLevels = useScopedUserPowerLevels(contextWorkspaceId);

  const createWorkspaceRole = async ({
    workspaceId,
    name,
    permissions,
    defaultRoleType,
  }: CreateWorkspaceRolePayload) => {
    if (!workspacePermissions.adjustRoles) return pushNoPermissionToast();
    if (!workspaceId) throw new Error('Cannot create workspace role: workspaceId is missing');

    setIsLoading(true);

    const powerLevels = getScopedPowerLevels(
      clampRoleEditorWorkspacePermissions(permissions, actorPowerLevels.workspace)
    );
    const newRole: WorkspaceRoleV2 = {
      id: uuid4(),
      name,
      powerLevels,
      ...(defaultRoleType && { defaultRoleType }),
    };

    try {
      await matrixClient.sendStateEvent(workspaceId, CustomMeedioMatrixEvents.WORKSPACE_ROLES, {
        version: WORKSPACE_ROLES_EVENT_VERSION,
        roles: [...workspaceRoles, newRole],
        assignments: roleAssignments,
      });
    } catch (error) {
      logger.error('Failed to create workspace role', error);
      pushToast({ variant: 'error', title: t('something_went_wrong_try_again_later') });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return { createWorkspaceRole, isLoading };
};

export default useCreateWorkspaceRole;
